/**
 * Shipping Package Limit Configuration & Volume Evaluation Utilities
 *
 * Synchronized with Bosta shipping package size tiers:
 * - Medium: <= 5,280 cm³
 * - Large:  <= 9,240 cm³ (Maximum allowable package size for standard shipping)
 * - XLarge: > 9,240 cm³ (Exceeds permitted checkout limit)
 *
 * Backend Rule: CartService throws OrderPackageLimitExceeded if packageSize > BostaPackageSize.LARGE.
 */

export const SHIPPING_PACKAGE_LIMITS = {
  // Maximum allowed shipping package volume in cm³ (Bosta LARGE threshold)
  MAX_PACKAGE_VOLUME_CM3: 9240,

  // Fallback volume in cm³ if a product's dimensions are zero or unconfigured
  // (e.g. standard apparel / towel fold: 20cm x 20cm x 2cm = 800 cm³)
  DEFAULT_ITEM_VOLUME_CM3: 800,
};

/**
 * Calculates single unit volume in cm³ for a cart item or product.
 * Checks item-level dimensions first, then nested product dimensions, then volume fallback.
 *
 * @param {Object} item - Cart item or product object
 * @returns {number} Volume in cm³
 */
export function getItemVolume(item) {
  if (!item) return 0;

  const length = Number(item.length ?? item.product?.length ?? 0);
  const width = Number(item.width ?? item.product?.width ?? 0);
  const height = Number(item.height ?? item.product?.height ?? 0);

  if (length > 0 && width > 0 && height > 0) {
    return length * width * height;
  }

  const explicitVolume = Number(item.volume ?? item.product?.volume ?? 0);
  if (explicitVolume > 0) {
    return explicitVolume;
  }

  return SHIPPING_PACKAGE_LIMITS.DEFAULT_ITEM_VOLUME_CM3;
}

/**
 * Calculates the total cumulative volume of an array of cart items in cm³.
 *
 * @param {Array} items - Array of cart items
 * @returns {number} Total volume in cm³
 */
export function calculateCartVolume(items) {
  if (!Array.isArray(items) || items.length === 0) return 0;

  return items.reduce((sum, item) => {
    const qty = Math.max(0, Number(item.quantity ?? item.qty ?? 1));
    return sum + getItemVolume(item) * qty;
  }, 0);
}

/**
 * Evaluates whether adding a given item (with quantity) will cause the cart to exceed the limit.
 *
 * @param {Array} currentItems - Current cart items
 * @param {Object} itemToAdd - Product or cart item being added
 * @param {number} quantityToAdd - Quantity to add (default: 1)
 * @returns {{ willExceed: boolean, currentVolume: number, nextVolume: number, remainingVolume: number, maxVolume: number }}
 */
export function willAddingExceedLimit(currentItems, itemToAdd, quantityToAdd = 1) {
  const currentVolume = calculateCartVolume(currentItems);
  const addedUnitVolume = getItemVolume(itemToAdd);
  const qty = Math.max(1, Number(quantityToAdd) || 1);
  const nextVolume = currentVolume + addedUnitVolume * qty;
  const maxVolume = SHIPPING_PACKAGE_LIMITS.MAX_PACKAGE_VOLUME_CM3;

  return {
    willExceed: nextVolume > maxVolume,
    currentVolume,
    nextVolume,
    remainingVolume: Math.max(0, maxVolume - currentVolume),
    maxVolume,
  };
}

/**
 * Evaluates whether updating an existing cart item's quantity will cause the cart to exceed the limit.
 *
 * @param {Array} currentItems - Current cart items
 * @param {string|number} itemId - ID of the item being updated
 * @param {number} nextQuantity - Proposed new quantity
 * @returns {{ willExceed: boolean, currentVolume: number, nextVolume: number, remainingVolume: number, maxVolume: number }}
 */
export function willQuantityChangeExceedLimit(currentItems, itemId, nextQuantity) {
  const items = Array.isArray(currentItems) ? currentItems : [];
  const currentVolume = calculateCartVolume(items);
  const maxVolume = SHIPPING_PACKAGE_LIMITS.MAX_PACKAGE_VOLUME_CM3;

  const nextItems = items.map((item) =>
    item.id === itemId
      ? { ...item, quantity: Math.max(0, Number(nextQuantity) || 0) }
      : item
  );

  const nextVolume = calculateCartVolume(nextItems);

  return {
    willExceed: nextVolume > maxVolume,
    currentVolume,
    nextVolume,
    remainingVolume: Math.max(0, maxVolume - currentVolume),
    maxVolume,
  };
}

/**
 * Returns true if the cart items currently exceed the max permitted shipping volume.
 *
 * @param {Array} items - Cart items
 * @returns {boolean}
 */
export function isCartOverShippingLimit(items) {
  return calculateCartVolume(items) > SHIPPING_PACKAGE_LIMITS.MAX_PACKAGE_VOLUME_CM3;
}

/**
 * Determines whether increasing a specific cart item by +1 would keep the cart within the limit.
 *
 * @param {Array} items - Cart items
 * @param {string|number} itemId - Target item ID
 * @returns {boolean}
 */
export function canIncreaseItemQuantity(items, itemId) {
  const list = Array.isArray(items) ? items : [];
  const target = list.find((i) => i.id === itemId);
  if (!target) return false;

  const currentQty = Math.max(1, Number(target.quantity ?? target.qty ?? 1));
  const check = willQuantityChangeExceedLimit(list, itemId, currentQty + 1);
  return !check.willExceed;
}
